<script lang="ts" setup>
import { vOnClickOutside } from "@vueuse/components";
import { ref, computed, nextTick } from "vue";
import { useRoute } from "vue-router";

import { useAlertModal } from "@/composables/useAlertModal.js";
import { useFiltersStore } from "@/store/filters/index.ts";
import { useModalStore } from "@/store/modal.js";
import { canAccess } from "@/utils/casl/access.ts";

const route = useRoute();
const filterStore = useFiltersStore();
const alertModalStore = useModalStore();

const { alertModal } = useAlertModal();

const props = withDefaults(
  defineProps<{

    routeItem: any
    depth?: number
    smallMenu?: boolean
    isOpen?: boolean
    index?: number | string
  }>(),
  {
    depth: 0,
    smallMenu: false,
  },
);

const showChildren = ref(false);
const expanded = ref(false);
const containerHeight = ref<string | number>(0);

const containerRef: any = ref(null);

const close = () => {
  if (!props.smallMenu) return;
  // showChildren.value = false;
  // expanded.value = false;
};

const toggleMenu = (parent?: boolean) => {
  if (alertModalStore.alertModal) {
    alertModal.open();
    return;
  }
  expanded.value = !expanded.value;
  if (!showChildren.value) {
    showChildren.value = true;
    nextTick(() => {
      containerHeight.value = containerRef.value.scrollHeight + "px";
      setTimeout(() => {
        containerHeight.value = "fit-content";
        if (navigator.userAgent.indexOf("Firefox") !== -1) {
          containerHeight.value = "-moz-max-content";
        }
        containerRef.value.style.overflow = "visible";
      }, 300);
    });
    if (parent) {
      filterStore.handleMobileMenu(true);
    }
    else {
      filterStore.handleMobileMenu(false);
    }
  }
  else {
    containerHeight.value = containerRef.value.scrollHeight + "px";
    containerRef.value.style.overflow = "hidden";
    setTimeout(() => {
      containerHeight.value = 0 + "px";
    }, 10);
    setTimeout(() => {
      showChildren.value = false;
    }, 300);
  }
};
const showLabel = computed(() => {
  return filterStore.isOpenNavMenu;
});
</script>

<template>
  <div
    v-if="
      props.routeItem.meta?.showAlways ||
        canAccess(props.routeItem.meta?.ability)
    "
    v-on-click-outside="close"
    :class="{ opened: expanded }"
    class="menu-item"
    tabindex="0"
    @keydown.enter="toggleMenu(false)"
  >
    <div
      :style="{
        'padding-left':
          filterStore.isOpenNavMenu || filterStore.isShowMobileMenu
            ? depth * 20 + 10 + 'px'
            : '0',
        'justify-content':
          filterStore.isOpenNavMenu || filterStore.isShowMobileMenu
            ? 'space-between'
            : 'center',
      }"
      class="label w-full"
      @click="toggleMenu(props.routeItem.meta?.parentMenu)"
    >
      <div
        :class="{
          'w-full': filterStore.isOpenNavMenu || filterStore.isShowMobileMenu,
        }"
      >
        <router-link
          v-if="!props.routeItem.meta?.parentMenu"
          :class="{
            'text-primary': route.name === routeItem.name,
            'cursor-not-allowed text-secondaryText':
              alertModalStore.alertModal,
          }"
          :to="!alertModalStore.alertModal ?
            { path: props.routeItem.path, query: route.query } : {} "
          class="flex gap-2 p-1 items-center text-[16px] justify-center w-full"
          tabindex="0"
        >
          <feather-icon
            v-if="props.routeItem.icon"
            :class="{ opened: expanded }"
            :type="props.routeItem.icon"
            size="20"
          />
          <span :class="!showLabel ? 'menu-text-hide' : 'menu-text-visible'">
            {{ props.routeItem?.meta.title }}
          </span>
        </router-link>
        <article
          v-else
          :class="{
            'text-primary': route.name === routeItem.name,
          }"
          class="flex gap-2 p-1 items-center text-[16px] justify-center w-full"
        >
          <feather-icon
            v-if="props.routeItem.icon"
            :class="{ opened: expanded }"
            :type="props.routeItem.icon"
            size="20"
          />

          <span :class="!showLabel ? 'menu-text-hide' : 'menu-text-visible'">
            {{ props.routeItem?.meta.title }}
          </span>
        </article>
      </div>
      <div
        v-if="props.routeItem.children && filterStore.isOpenNavMenu"
        class="right pr-5"
      >
        <feather-icon
          :class="{ opened: expanded }"
          class="expand-icon expand font-light"
          size="15"
          type="chevron-down"
        />
      </div>
    </div>
    <div
      v-show="showChildren"
      ref="containerRef"
      :style="{ height: containerHeight }"
      class="items-container"
      @keydown.enter="toggleMenu(false)"
    >
      <MenuItem
        v-for="(item, index) in props.routeItem.children"
        :key="index"
        :class="{ opened: showChildren }"
        :depth="depth + 1"
        :route-item="item"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.menu-item {
  position: relative;

  .label {
    @apply text-secondaryText font-normal w-full;
    width: 100%;
    display: flex;
    white-space: nowrap;
    height: 50px;
    box-sizing: border-box;
    font-size: 14px;
    transition: all 0.3s ease;

    > div {
      display: flex;
      align-items: center;
      gap: 10px;

      span {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;

        &.menu-text-hide {
          width: 0;
          opacity: 0;
        }

        &.menu-text-visible {
          width: 100%;
          opacity: 100;
          transition: width 0.1s;
        }
      }
    }

    i {
      font-size: 20px;
      transition: all 0.3s ease;
      min-width: 20px;

      &.expand {
        font-size: 16px;

        &.opened {
          transform: rotate(180deg);
        }
      }
    }

    &.small-item {
      width: fit-content;
    }

    &:hover {
      @apply hover:bg-hover cursor-pointer;
    }
  }

  .items-container {
    width: 100%;
    left: calc(100% + 6px);
    transition: height 0.3s ease;
    overflow: hidden;

    &.small-menu {
      width: fit-content;
      top: 0;
      @apply w-full;
      .label {
        width: 100% !important;
        @apply flex justify-center items-center;
        span {
          display: none;
        }
      }
    }
  }
}
</style>
